import React, { useState } from 'react';
import { hot } from 'react-hot-loader/root';
import css from './CheckBackSoon.scss';
import { SFService } from 'utils/types';
import { getServiceURL } from 'utils/deepLinks';
import { camelCase } from 'lodash';
import { Card } from 'semantic-ui-react';
import Icon from 'SF/MaterialIcon';
import { HTMLtoText, isNonEmptyString } from 'utils/text';

/**
 * Generates a card that contains the name, description, and links to a specific service.
 *
 * @param {SFEntityID} id - the unique identifier for the service.
 * @param {string} name - the name of the service.
 * @param {string} description - a student facing description of the service.
 * @param {string} displayName - an optional, alternative name to display to end users instead of the name.
 * @returns {JSX.Element} - the card representing the service
 */
const ServiceCard = ({service: {id, name, description, displayName}}) => {
    const [ readMore, setReadMore ] = useState(false);

    /**
     * For text that goes over 120 characters, truncates the length and
     * provides a toggle to display the full length or shortened version.
     *
     * @param {string} text - the text to be displayed
     * @returns {JSX.Element} the adjusted text and toggle link
     */
    const truncateDescription = (text) => {
        if (text.length <= 120) return <>{text}</>;
        return (
            <>
                {readMore ? text : `${text.substring(0, 120)}...`}
                <a role="button" tabIndex="0" onClick={() => setReadMore(!readMore)}>
                    {readMore ? 'Read Less' : 'Read More'}
                </a>
            </>
        );
    };

    const getName = () => isNonEmptyString(displayName) ? displayName : name;

    return (
        <Card fluid id={camelCase("serviceCard " + getName())}>
            <Card.Content className={css['card-content-container']}>
                <div className={css['color-bar']} />
                <Card.Content className={css['card-content']}>
                    <Card.Header className={css['title']}>
                        <a aria-label="Visit Service" href={getServiceURL(id)}>
                            {name.length < 75 ? getName() : `${getName().substring(0, 75)}...`}
                        </a>
                    </Card.Header>
                    <Card.Description className={css['description']}>
                        {truncateDescription(HTMLtoText(description))}
                    </Card.Description>
                </Card.Content>
            </Card.Content>
            <Card.Content extra className={css['button']}>
                <a href={getServiceURL(id)}>Visit Service <Icon icon="keyboard_arrow_right" aria-label="Visit Service" /></a>
            </Card.Content>
        </Card>
    )
}

ServiceCard.propTypes = {
    service: SFService.isRequired
}

export default hot(ServiceCard);
